<template>
  <PrimaryModal @clickBackground="close()">
    <div class="modal-size max-w-screen-lg box-border select-none">
      <div class="flex w-full h-full">
        <div class="bg-primary-500 dark:bg-primary-600 px-10 py-10 flex justify-center items-center" style="width: 30%">
          <div>
            <div class="text-3xl font-bold text-white">{{ $t('message.beforeSingup') }}</div>
            <div class="text-lg font-bold text-white mt-10">{{ $t('message.readTerms') }}</div>
            <a
              class="underline text-lg font-bold text-white hover:text-gray-300"
              href="https://onlinejudge.u-aizu.ac.jp/term_of_use"
              target="_blank"
            >
              {{ $t('message.termOfUse') }}
            </a>
          </div>
        </div>
        <div
          class="overflow-y-scroll overflow-x-hidden pt-8 mx-2 my-5 flex justify-center"
          :class="isLightMode() ? 'custom-scrollbar' : 'custom-scrollbar-dark'"
          style="width: 70%"
        >
          <div class="mr-5">
            <div class="mb-10 text-2xl font-bold">{{ $t('message.signup') }}</div>
            <div class="flex mt-4">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.accountId') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setId"
              :radius="'0.5rem'"
              :value="userRegistrationStatePresenter.id.value"
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1">
              {{ $t('message.AlphabetsDigitsSymbols') }}
            </div>
            <div class="flex mt-6">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.password') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setPassword"
              :radius="'0.5rem'"
              :type="'password'"
              :value="userRegistrationStatePresenter.password.value"
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1"> {{ $t('message.moreThan4') }} </div>
            <div class="flex mt-4">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.confirmPassword') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setConfirmPassword"
              :radius="'0.5rem'"
              :type="'password'"
              :value="userRegistrationStatePresenter.confirmPassword.value"
            />
            <div class="flex mt-6">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.name') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setName"
              :radius="'0.5rem'"
              :value="userRegistrationStatePresenter.name.value"
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1">
              {{ $t('message.AlphabetsDigitsSymbolsSpace') }}
            </div>
            <div class="mt-6 text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.affiliation') }}</div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setAffiliation"
              :radius="'0.5rem'"
              :value="
                userRegistrationStatePresenter.affiliation.value == null
                  ? ''
                  : userRegistrationStatePresenter.affiliation.value
              "
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1">
              {{ $t('message.AlphabetsDigitsSymbolsSpace') }}
            </div>
            <div class="mt-6 text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.avatar') }}</div>
            <div class="flex">
              <div class="relative mt-2">
                <input
                  id="avatar-input"
                  accept="image/*"
                  class="hidden"
                  type="file"
                  @change="event => userRegistrationStatePresenter.uploadAvatar(event as InputEvent)"
                />
                <label
                  class="cursor-pointer shadow-lg px-6 bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 dark:active:bg-primary-800"
                  for="avatar-input"
                  style="
                    border-radius: 0.5rem;
                    transition: background 0.25s;
                    font-weight: 700;
                    padding-top: 11px;
                    padding-bottom: 11px;
                  "
                >
                  {{ $t('message.selectImage') }}
                </label>
              </div>
              <IconTextButton
                :border_radius="0.5"
                class="mx-2"
                :font_weight="'700'"
                :icon="'delete'"
                :iconSize="'text-xl'"
                :padding_bottom="4"
                :padding_left="10"
                :padding_right="10"
                :padding_top="4"
                :text="$t('message.delete')"
                :textSize="'text-base'"
                @click="userRegistrationStatePresenter.setAvatar(null)"
              />
            </div>
            <div v-if="userRegistrationStatePresenter.avatar.value" class="mt-2">
              <img :src="userRegistrationStatePresenter.avatar.value" style="max-width: 200px; max-height: 200px" />
            </div>
            <div class="flex mt-6">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.country') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <CountryDropDown
              :itemList="userRegistrationStatePresenter.countryList"
              :selected="userRegistrationStatePresenter.setCountryIndex"
              :selectedIndex="userRegistrationStatePresenter.countryIndex.value"
            />
            <div class="flex mt-6">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.displayLanguage') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <DropDown
              :itemList="userRegistrationStatePresenter.displayLanguageList"
              :selected="userRegistrationStatePresenter.setDisplayLanguageIndex"
              :selectedIndex="userRegistrationStatePresenter.displayLanguageIndex.value"
            />
            <div class="flex mt-6">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.programmingLanguage') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <ScrollableDropDown
              :itemList="userRegistrationStatePresenter.programmingLanguageList"
              :selected="userRegistrationStatePresenter.setDefaultProgrammingLanguageIndex"
              :selectedIndex="userRegistrationStatePresenter.defaultProgrammingLanguageIndex.value"
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1">
              {{ $t('message.defaultLanguageSettings') }}
            </div>
            <div class="flex mt-6">
              <div class="text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.sourceCodePolicy') }}</div>
              <div class="text-red-500 mb-1 ml-1">*</div>
            </div>
            <DropDown
              :itemList="userRegistrationStatePresenter.policyList"
              :selected="userRegistrationStatePresenter.setPolicyIndex"
              :selectedIndex="userRegistrationStatePresenter.policyIndex.value"
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1">
              {{ $t('message.sourceCodePolicyDescription') }}
            </div>
            <div class="mt-6 text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.birthYear') }}</div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setBirthYear"
              :radius="'0.5rem'"
              :type="'number'"
              :value="
                userRegistrationStatePresenter.birthYear.value == null
                  ? ''
                  : userRegistrationStatePresenter.birthYear.value.toString()
              "
            />
            <div class="mt-6 text-gray-500 dark:text-dark-text-dark mb-1">{{ $t('message.email') }}</div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setMail"
              :radius="'0.5rem'"
              :type="'email'"
              :value="
                userRegistrationStatePresenter.mail.value == null ? '' : userRegistrationStatePresenter.mail.value
              "
            />
            <div class="mt-6 text-gray-500 dark:text-dark-text-dark mb-1">URL</div>
            <SingleLineInputArea
              :onChange="userRegistrationStatePresenter.setUrl"
              :radius="'0.5rem'"
              :value="userRegistrationStatePresenter.url.value == null ? '' : userRegistrationStatePresenter.url.value"
            />
            <div class="text-gray-400 dark:text-dark-text-dark text-sm mt-1">
              {{ $t('message.yourURL') }}
            </div>
            <div class="flex justify-center mt-8">
              <IconTextButton
                :border_radius="2"
                class="mx-1"
                :disabled="authStatePresenter.isLoading.value"
                :font_weight="'700'"
                :icon="'person_add'"
                :iconSize="'text-xl'"
                :padding_bottom="4"
                :padding_left="10"
                :padding_right="10"
                :padding_top="4"
                :text="$t('message.createNewAccount')"
                :textSize="'text-base'"
                @click="registerUser"
              />
            </div>
            <div style="height: 36px" />
          </div>
        </div>
      </div>
    </div>
  </PrimaryModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import CountryDropDown from './children/CountryDropDown.vue'
import DropDown from './children/DropDown.vue'
import ScrollableDropDown from './children/ScrollableDropDown.vue'

import { UserRegistrationRequest } from '@/domain/entities/user/userCreateRequest'
import IconTextButton from '@/presentation/components/Button/IconTextButton.vue'
import SingleLineInputArea from '@/presentation/components/Input/SingleLineInputArea.vue'
import PrimaryModal from '@/presentation/components/Modal/PrimaryModal.vue'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useUserRegistrationStatePresenter } from '@/presentation/composables/userRegistration/useUserRegistrationStatePresenter'

export default defineComponent({
  components: { PrimaryModal, SingleLineInputArea, IconTextButton, CountryDropDown, DropDown, ScrollableDropDown },
  setup(props, ctx) {
    const modalStatePresenter = useModalStatePresenter()
    const authStatePresenter = useAuthStatePresenter()
    const userRegistrationStatePresenter = useUserRegistrationStatePresenter()
    const { isLightMode, displayLanguage } = useConfigStatePresenter()

    const registerUser = async () => {
      if (!userRegistrationStatePresenter.checkValues(displayLanguage.value)) {
        return
      }
      const requestUser: UserRegistrationRequest = {
        id: userRegistrationStatePresenter.id.value,
        name: userRegistrationStatePresenter.name.value,
        password: userRegistrationStatePresenter.password.value,
        affiliation: userRegistrationStatePresenter.affiliation.value,
        country: userRegistrationStatePresenter.countryList[userRegistrationStatePresenter.countryIndex.value].value,
        displayLanguage:
          userRegistrationStatePresenter.displayLanguageList[userRegistrationStatePresenter.displayLanguageIndex.value],
        defaultProgrammingLanguage:
          userRegistrationStatePresenter.programmingLanguageList[
            userRegistrationStatePresenter.defaultProgrammingLanguageIndex.value
          ],
        birthYear: userRegistrationStatePresenter.birthYear.value,
        avatar: userRegistrationStatePresenter.avatar.value,
        mail: userRegistrationStatePresenter.mail.value,
        url: userRegistrationStatePresenter.url.value,
        policy: userRegistrationStatePresenter.policyList[userRegistrationStatePresenter.policyIndex.value]
      }
      await authStatePresenter.registerUser(requestUser)
    }

    const close = () => {
      modalStatePresenter.setIsShowUserRegistrationModal(false)
    }

    return {
      registerUser,
      isLightMode,
      authStatePresenter,
      userRegistrationStatePresenter,
      close
    }
  }
})
</script>

<style lang="postcss" scoped>
.modal-size {
  width: 60vw;
  height: 80vh;
}
</style>
